import React from 'react';
import {
  JUDO_GROUPS,
  getJudoSubgroupsFor,
  getJudoTechniquesFor,
} from '../../data/combativesJudo';
import SelectWithOther from './SelectWithOther';

/**
 * JudoClassificationPicker — three-stage cascading select
 *   group → subgroup → named technique (with free-text fallback).
 *
 * Mirrors PositionPicker's UX for BJJ, but the data shape is
 * intentionally different: Judo classifies by mechanism, not by
 * position context. The third stage is a `datalist` so the user can
 * pick from the curated list OR type a non-listed technique.
 */
const JudoClassificationPicker = ({ value, onChange, testId }) => {
  const v = value || { group: '', subgroup: '', name: '' };
  const subgroups = getJudoSubgroupsFor(v.group);
  const techniques = getJudoTechniquesFor(v.group, v.subgroup);
  const subgroupDef = subgroups.find((s) => s.subgroup === v.subgroup);

  const update = (patch) => onChange({ ...v, ...patch });

  return (
    <div className="space-y-2">
      {/* Group */}
      <select
        value={v.group}
        onChange={(e) => update({ group: e.target.value, subgroup: '', name: '' })}
        className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
        data-testid={`${testId}-group`}
      >
        <option value="">Select group…</option>
        {JUDO_GROUPS.map((g) => (
          <option key={g.group} value={g.group}>{g.groupLabel}</option>
        ))}
      </select>

      {/* Subgroup (visible only after group chosen) */}
      {v.group && (
        <select
          value={v.subgroup}
          onChange={(e) => update({ subgroup: e.target.value, name: '' })}
          className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
          data-testid={`${testId}-subgroup`}
        >
          <option value="">Select subgroup…</option>
          {subgroups.map((s) => (
            <option key={s.subgroup} value={s.subgroup}>{s.subgroupLabel}</option>
          ))}
        </select>
      )}

      {/* Optional help text on the subgroup (e.g., Kansetsu-waza elbow-only note) */}
      {subgroupDef?.helpText && (
        <p className="text-[10px] text-enf-text-muted italic leading-relaxed">{subgroupDef.helpText}</p>
      )}

      {/* Technique — native select with "Other (specify)" fallback so
          the dropdown matches the group/subgroup styling exactly,
          re-opens cleanly, and respects Adaptive Mode palette. */}
      {v.subgroup && (
        <SelectWithOther
          value={v.name}
          onChange={(name) => update({ name })}
          suggestions={techniques}
          placeholder="Select technique…"
          testId={`${testId}-name`}
        />
      )}
    </div>
  );
};

export default JudoClassificationPicker;
